/**
 * Email contract (F26): the caller's email settings, newsletter consent and
 * Resend's webhook. Re-exported from index.ts.
 *
 * Account emails (welcome, trial and subscription changes) are part of the
 * service and cannot be switched off; the newsletter is opt-in only
 * (MAIL-043). The consent wording is versioned: change the text, change the
 * version, so every audited opt-in names the words the user agreed to.
 *
 * Owner: email (F26). Implements MAIL-040 to MAIL-043.
 */

export const EMAIL_ROUTES = {
  /** GET -> 200 EmailSettingsResponse; PUT EmailSettingsRequest -> 200 EmailSettingsResponse (MAIL-040, MAIL-041). */
  emailSettings: '/api/v1/me/email',
  /** POST, raw Resend event body with Svix signature headers (MAIL-060). Called by Resend only. */
  resendWebhook: '/api/resend/webhook',
} as const;

/** The checkbox label, shown wherever the newsletter is offered (MAIL-044, MAIL-045). */
export const NEWSLETTER_CONSENT_TEXT = 'Email me Bioterafi news: new features, tips and offers. I can unsubscribe at any time.';
/** Recorded with every choice (MAIL-041). Change it whenever NEWSLETTER_CONSENT_TEXT changes. */
export const NEWSLETTER_CONSENT_VERSION = '2026-10-15';

/** Where the user made the choice. */
export type NewsletterSource = 'account' | 'trial_started';
export const NEWSLETTER_SOURCES: readonly NewsletterSource[] = ['account', 'trial_started'];

export interface EmailSettingsRequest {
  newsletter: boolean;
  source: NewsletterSource;
}

export interface NewsletterSettings {
  /** The deployment can send newsletters and the account has a verified address. */
  available: boolean;
  subscribed: boolean;
  /** When the user last chose, by any route (including an unsubscribe link); null while they never have. */
  decidedAt: string | null;
  consentText: string;
}

export interface EmailSettingsResponse {
  /** The deployment sends account emails (MAIL-002). */
  accountEmails: boolean;
  /** The account has a verified email address (PRIV-013). */
  hasAddress: boolean;
  newsletter: NewsletterSettings;
  /** Email to the current address is stopped after a hard bounce or a spam complaint (MAIL-064). */
  deliveryProblem: 'bounce' | 'complaint' | null;
}
